import React, { forwardRef } from "react";
import classNames from "classnames";
import styles from "./PhoneInput.module.scss";

import {
  PhonenumberFields,
  isValidSaudiPhoneNumber,
} from "../../../setup/util/phoneNumber.helpers";
import { validateNumber } from "../../../setup/util/helpers";
import { useTranslation } from "react-i18next";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  errorMessage?: string;
  withFlag?: boolean;
}

const PhoneInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      onBlur,
      name,
      errorMessage,
      withFlag,
      label,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const internalRef = React.useRef<HTMLInputElement | null>(null);

    const [hasValue, setHasValue] = React.useState(!!(value || defaultValue));

    React.useEffect(() => {
      setHasValue(!!(value || defaultValue || internalRef.current?.value));
    }, [value, defaultValue]);

    const hasError =
      typeof value === "string" &&
      value.length > 0 &&
      !isValidSaudiPhoneNumber(value);

    return (
      <div style={{ position: "relative" }}>
        <div className={styles["container"]}>
          <div className={styles["country_selector"]}>
            {withFlag && (
              <img
                className={styles["country_selector_flag"]}
                src={PhonenumberFields["SA"]?.flag}
                alt={PhonenumberFields["SA"]?.name}
              />
            )}
            <img
              className={styles["country_selector_pointer"]}
              src={"assets/images/svg/arrow_outline.svg"}
              alt={"arrow"}
            />
            <p className={styles["phone_input_prefix"]}>
              {PhonenumberFields["SA"]?.prefix}
            </p>
          </div>

          <div
            className={classNames(
              styles["phone_input"],
              hasError && styles["error_bg"]
            )}>
            {/* Label inside the container, only shown if hasValue is true */}
            {label && hasValue && (
              <div className={styles.header}>
                <p>{label}</p>
              </div>
            )}

            <div className={styles.input_wrapper}>
              <input
                ref={(el) => {
                  internalRef.current = el;
                  if (typeof ref === "function") ref(el);
                  else if (ref && "current" in ref) ref.current = el;
                }}
                name={name}
                className={styles["phone_input_field"]}
                {...props}
                value={value}
                defaultValue={defaultValue}
                onChange={(e) => {
                  setHasValue(!!e.target.value);
                  onChange?.(e);
                }}
                onBlur={onBlur}
                onKeyPress={(event) => {
                  if (
                    event.key === " " ||
                    event.key === "e" ||
                    event.key === "+" ||
                    event.key === "-" ||
                    event.key === "." ||
                    !validateNumber(event.key)
                  ) {
                    event.preventDefault();
                  }
                }}
                inputMode="numeric"
                type="tel"
                placeholder={t("lbl_enter_phone_number_hint")}
              />
            </div>
          </div>
        </div>
        {errorMessage && (
          <div className={styles["error_container"]}>
            <p dir={"rtl"} className={styles["error_text"]}>
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export default React.memo(PhoneInput);
